import React from "react";
import CardMedia from "@mui/material/CardMedia";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { IIndicatorDataset } from "@/ChnlListingPageV2/chnlListingPage.props";

const bps = DtvTheme.breakpoints;

interface AddOnInfoItemProps {
  id: string;
  src: string;
  alt: string;
  textContent: string;
  textColor?: string;
}

const AddOnInfoItem = ({ id, src, alt, textContent, textColor }: AddOnInfoItemProps) => (
  <DtvBox id={id} sx={{
    display: "flex",
    alignItems: "center",
    padding: "3px 0px 3px 0px",
    marginBottom: "6px !important",
    fontSize: "14px",
    borderBottom: "0px !important"
  }}>
    <CardMedia alt={alt} component="img"
      src={src}
      sx={{
        height: "24px",
        width: "24px",
        verticalAlign: "middle",
        display: "inline-flex"
      }}
    />
    <DtvText content={textContent}
      variant="smalltext"
      sx={{
        paddingLeft: "15px",
        [bps.down(bps.values.sm)]: {
          paddingLeft: "12.5px"
        },
        color: textColor
      }}
  />
  </DtvBox>
);

export interface AddOnPromoLegendProps {
  id: string;
  addOnPromoLogoInfo?: IIndicatorDataset;
  textColor?: string;
  /** Margin override for the wrapper only — deliberately narrower than an open `sx`
   * prop, so a caller can't override the flex layout/breakpoint stacking/gap this
   * component just unified across its two consumers. A per-breakpoint map, matching
   * the only shape its one caller (CategoryHeader.tsx) actually passes; constrained
   * to the real breakpoint keys so `{ marginTop: "…" }` or a typo'd key doesn't
   * typecheck and silently do nothing. */
  margin?: Partial<Record<"xs" | "sm" | "md" | "lg" | "xl", string>>;
  /** [WAT-11717] Per-item toggle — lets a caller (SearchResultsMessage, for the
   * search-results view) hide a legend item when no channel carrying that indicator
   * is actually present in what's rendered below it. All default to `true`, so
   * CategoryHeader's browse-view mount (which doesn't pass these — its Add-On section
   * legend is always relevant to the category it heads) is unaffected. */
  showIncluded?: boolean;
  showAddOn?: boolean;
  showPromo?: boolean;
}

/**
 * @description [WAT-11256] The three-item "included / add-on / promo" icon legend,
 * shared by CategoryHeader's expanded Add-On section header and SearchResultsMessage
 * (the search-flat-list AC2 banner). Both consumers only ever had the same wrapper
 * layout, the same three hardcoded icon URLs, and the same three fallback copy
 * strings — this is the whole shared contract, not just the innermost row. The icon
 * URLs are hardcoded (not CMS-authored, unlike the copy next to them) because they're
 * DTV brand assets shared across every lineup story, not per-page content.
 *
 * @param id - root element id AND the prefix the three child item ids are derived
 *   from (`${id}-included`/`-add-on`/`-promo`) — two mounts sharing an `id` will
 *   collide on those child DOM ids, so callers must pass a unique one per mount.
 * @param addOnPromoLogoInfo - CMS-authored legend copy; each field defends its own
 *   fallback, so this whole prop is optional.
 * @param textColor - applied to every legend item's icon-adjacent text.
 * @param margin - see AddOnPromoLegendProps.
 */
const AddOnPromoLegend = ({
  id,
  addOnPromoLogoInfo,
  textColor,
  margin,
  showIncluded = true,
  showAddOn = true,
  showPromo = true
}: AddOnPromoLegendProps) => (
  <DtvBox id={id} sx={{
    width: "100%",
    display: "flex !important",
    [bps.down(bps.values.md)]: {
      flexDirection: "column !important"
    },
    [bps.up(bps.values.md)]: {
      alignItems: "center",
      gap: "20px"
    },
    margin
  }}>
    {/* [WAT-12043 / YFH-AMP105] All three legend icons are alt="" —
        decorative. Each icon sits immediately next to a <DtvText> that
        carries its meaning, so a descriptive alt would produce a double
        announcement (icon alt + adjacent text). Per WCAG 1.1.1 the icons
        are removed from the accessibility tree via alt="". */}
    {showIncluded && (
      <AddOnInfoItem
        alt={""}
        id={`${id}-included`}
        src={"https://www.directv.com/dtvassets/global/icons/component-controls/legend_check.svg"}
        textColor={textColor}
        textContent={addOnPromoLogoInfo?.included || "Channel is included in package"}
      />
    )}
    {showAddOn && (
      <AddOnInfoItem
        alt={""}
        id={`${id}-add-on`}
        src={"https://www.directv.com/dtvassets/global/icons/component-controls/legend_dollar.svg"}
        textColor={textColor}
        textContent={addOnPromoLogoInfo?.addon || "Add-on channels (Channel can be added for an additional cost)"}
      />
    )}
    {showPromo && (
      <AddOnInfoItem
        alt={""}
        id={`${id}-promo`}
        src={"https://www.directv.com/dtvassets/global/icons/component-controls/legend_promotion.svg"}
        textColor={textColor}
        textContent={addOnPromoLogoInfo?.promo || "Promotions (Channel is included for an introductory time)"}
      />
    )}
  </DtvBox>
);

export const MemoizedAddOnPromoLegend = React.memo(AddOnPromoLegend);
